import React, { useEffect, useMemo, useRef, useState } from "react";
import "./InterviewerFeedbackPage.css";

function getFeedbackToken() {
  if (typeof window === "undefined") {
    return "";
  }

  const params = new URLSearchParams(window.location.search || "");
  const queryToken = (params.get("token") || "").trim();
  if (queryToken) {
    return queryToken;
  }

  return String(window.__INTERVIEWER_FEEDBACK_TOKEN__ || "").trim();
}

function buildErrorMessage(payload, fallbackMessage) {
  if (payload && typeof payload.error === "string" && payload.error.trim()) {
    return payload.error.trim();
  }
  return fallbackMessage;
}

function joinVoiceText(...parts) {
  return parts
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

function collapseProgressiveVoiceSegments(segments) {
  const collapsed = [];

  segments.forEach((segment) => {
    const normalizedSegment = String(segment || "").trim();
    if (!normalizedSegment) {
      return;
    }

    const previousSegment = collapsed[collapsed.length - 1];
    if (!previousSegment) {
      collapsed.push(normalizedSegment);
      return;
    }

    const safePreviousSegment = previousSegment.toLowerCase();
    const safeCurrentSegment = normalizedSegment.toLowerCase();
    if (
      safeCurrentSegment === safePreviousSegment ||
      safeCurrentSegment.startsWith(safePreviousSegment) ||
      safePreviousSegment.startsWith(safeCurrentSegment)
    ) {
      collapsed[collapsed.length - 1] =
        normalizedSegment.length >= previousSegment.length
          ? normalizedSegment
          : previousSegment;
      return;
    }

    collapsed.push(normalizedSegment);
  });

  return collapsed;
}

function replaceAnswer(answers, questionId, nextAnswer) {
  return answers.map((item) =>
    item.id === questionId
      ? {
          ...item,
          answer: nextAnswer,
        }
      : item
  );
}

function normalizeSessionAnswers(session) {
  const storedAnswers = Array.isArray(session?.answers) ? session.answers : [];
  const questionDefinitions =
    Array.isArray(session?.questions) && session.questions.length > 0
      ? session.questions
      : storedAnswers;

  const storedAnswersById = new Map(
    storedAnswers
      .map((item) => [String(item?.id || "").trim(), item])
      .filter(([id]) => Boolean(id))
  );

  const normalizedAnswers = questionDefinitions.map((question, index) => {
    const id =
      String(question?.id || "").trim() || `feedback-question-${index + 1}`;
    const storedItem = storedAnswersById.get(id) || {};
    const label =
      String(question?.label || storedItem?.label || "").trim() ||
      `Question ${index + 1}`;
    const prompt =
      String(
        question?.prompt ||
          question?.question ||
          storedItem?.question ||
          label
      ).trim() || label;

    return {
      id,
      label,
      question: prompt,
      answer: String(storedItem?.answer || question?.answer || ""),
      required: Boolean(question?.required) || id === "interviewer_name",
    };
  });

  storedAnswers.forEach((item, index) => {
    const id = String(item?.id || "").trim();
    if (!id || normalizedAnswers.some((answer) => answer.id === id)) {
      return;
    }
    normalizedAnswers.push({
      id,
      label:
        String(item?.label || "").trim() || `Question ${normalizedAnswers.length + index + 1}`,
      question:
        String(item?.question || item?.label || "").trim() ||
        `Question ${normalizedAnswers.length + index + 1}`,
      answer: String(item?.answer || ""),
      required: false,
    });
  });

  return normalizedAnswers;
}

function speechErrorMessage(errorCode) {
  switch (String(errorCode || "").trim().toLowerCase()) {
    case "audio-capture":
      return "No microphone input was detected. Please check your microphone and try again.";
    case "network":
      return "Speech recognition hit a network issue. Please try again.";
    case "not-allowed":
    case "service-not-allowed":
      return "Microphone permission was blocked. Please allow microphone access and try again.";
    case "no-speech":
      return "No speech was detected. Please speak again.";
    default:
      return "We could not capture your voice just now. Please try again or type your answer.";
  }
}

export default function InterviewerFeedbackPage() {
  const token = useMemo(() => getFeedbackToken(), []);
  const SpeechRecognitionConstructor = useMemo(() => {
    if (typeof window === "undefined") {
      return null;
    }
    return window.SpeechRecognition || window.webkitSpeechRecognition || null;
  }, []);
  const speechRecognitionSupported = Boolean(SpeechRecognitionConstructor);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [formError, setFormError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [speechError, setSpeechError] = useState("");
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [listeningQuestionId, setListeningQuestionId] = useState("");
  const [speakingQuestionId, setSpeakingQuestionId] = useState("");

  const answersRef = useRef([]);
  const recognitionRef = useRef(null);
  const recognitionSessionRef = useRef({
    questionId: "",
    baseText: "",
    finalSegments: [],
  });
  const pendingListeningQuestionIdRef = useRef("");
  const startListeningRef = useRef(null);

  const isSubmitted = String(session?.status || "").trim().toLowerCase() === "submitted";

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    if (!token) {
      setLoadError("This feedback link is missing its token.");
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;

    async function loadSession() {
      setIsLoading(true);
      setLoadError("");
      try {
        const response = await fetch(
          `/api/meeting/interviewer-feedback/session?token=${encodeURIComponent(token)}`
        );
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || payload?.success === false) {
          throw new Error(buildErrorMessage(payload, "Failed to load the feedback session."));
        }
        if (cancelled) {
          return;
        }
        setSession(payload.session || null);
        setAnswers(normalizeSessionAnswers(payload.session || {}));
      } catch (error) {
        if (!cancelled) {
          setLoadError(error.message || "Failed to load the feedback session.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadSession();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const stopListening = (clearPendingQuestion = true) => {
    if (clearPendingQuestion) {
      pendingListeningQuestionIdRef.current = "";
    }
    const activeRecognition = recognitionRef.current;
    if (!activeRecognition) {
      setListeningQuestionId("");
      recognitionSessionRef.current = {
        questionId: "",
        baseText: "",
        finalSegments: [],
      };
      return;
    }

    try {
      activeRecognition.stop();
    } catch (error) {
      recognitionRef.current = null;
      setListeningQuestionId("");
      recognitionSessionRef.current = {
        questionId: "",
        baseText: "",
        finalSegments: [],
      };
    }
  };

  const startListening = (questionId) => {
    if (!SpeechRecognitionConstructor || isSubmitted) {
      return;
    }

    const activeAnswer =
      answersRef.current.find((item) => item.id === questionId)?.answer || "";
    const recognition = new SpeechRecognitionConstructor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.maxAlternatives = 1;

    recognitionSessionRef.current = {
      questionId,
      baseText: activeAnswer,
      finalSegments: [],
    };

    recognition.onstart = () => {
      setSpeechError("");
      setFormError("");
      setStatusMessage("");
      setListeningQuestionId(questionId);
    };

    recognition.onresult = (event) => {
      const recognitionState = recognitionSessionRef.current;
      if (recognitionState.questionId !== questionId) {
        return;
      }

      const finalSegments = Array.isArray(recognitionState.finalSegments)
        ? [...recognitionState.finalSegments]
        : [];
      const rawInterimSegments = [];

      for (let index = 0; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = String(result?.[0]?.transcript || "").trim();
        if (!transcript) {
          continue;
        }
        if (result.isFinal) {
          finalSegments[index] = transcript;
        } else {
          rawInterimSegments.push(transcript);
        }
      }

      const spokenSegments = collapseProgressiveVoiceSegments(
        [
          ...finalSegments.filter(Boolean),
          ...rawInterimSegments,
        ]
      );

      recognitionSessionRef.current = {
        ...recognitionState,
        finalSegments,
      };

      const nextAnswer = joinVoiceText(recognitionState.baseText, ...spokenSegments);
      setAnswers((currentAnswers) =>
        replaceAnswer(currentAnswers, questionId, nextAnswer)
      );
    };

    recognition.onerror = (event) => {
      setSpeechError(speechErrorMessage(event?.error));
    };

    recognition.onend = () => {
      if (recognitionRef.current === recognition) {
        recognitionRef.current = null;
      }

      const recognitionState = recognitionSessionRef.current;
      if (recognitionState.questionId === questionId) {
        const finalAnswer = joinVoiceText(
          recognitionState.baseText,
          ...((recognitionState.finalSegments || []).filter(Boolean))
        );
        if (finalAnswer) {
          setAnswers((currentAnswers) =>
            replaceAnswer(currentAnswers, questionId, finalAnswer)
          );
        }
      }

      recognitionSessionRef.current = {
        questionId: "",
        baseText: "",
        finalSegments: [],
      };
      setListeningQuestionId("");

      const nextQuestionId = pendingListeningQuestionIdRef.current;
      pendingListeningQuestionIdRef.current = "";
      if (nextQuestionId) {
        window.setTimeout(() => {
          if (startListeningRef.current) {
            startListeningRef.current(nextQuestionId);
          }
        }, 0);
      }
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch (error) {
      recognitionRef.current = null;
      setListeningQuestionId("");
      setSpeechError("Microphone start failed. Please try again or type your answer.");
    }
  };

  startListeningRef.current = startListening;

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (error) {
          // Ignore cleanup failures from browser speech APIs.
        }
      }
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const toggleListening = (questionId) => {
    setSpeechError("");
    if (listeningQuestionId === questionId) {
      stopListening();
      return;
    }

    if (recognitionRef.current) {
      pendingListeningQuestionIdRef.current = questionId;
      stopListening(false);
      return;
    }

    startListening(questionId);
  };

  const toggleReadAloud = (questionId, text) => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      setSpeechError("Read aloud is not available in this browser.");
      return;
    }

    if (speakingQuestionId === questionId) {
      window.speechSynthesis.cancel();
      setSpeakingQuestionId("");
      return;
    }

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
    }

    if (typeof window.SpeechSynthesisUtterance !== "function") {
      setSpeechError("Read aloud is not available in this browser.");
      return;
    }

    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.onstart = () => {
      setSpeechError("");
      setSpeakingQuestionId(questionId);
    };
    utterance.onend = () => {
      setSpeakingQuestionId("");
    };
    utterance.onerror = () => {
      setSpeakingQuestionId("");
      setSpeechError("We could not read that question aloud.");
    };
    window.speechSynthesis.speak(utterance);
  };

  const handleAnswerChange = (questionId, nextValue) => {
    setFormError("");
    setStatusMessage("");
    setAnswers((currentAnswers) =>
      replaceAnswer(currentAnswers, questionId, nextValue)
    );
  };

  const handleSaveDraft = async () => {
    if (!token || isSubmitted) {
      return;
    }

    stopListening();
    setIsSaving(true);
    setFormError("");
    setStatusMessage("");

    try {
      const response = await fetch("/api/meeting/interviewer-feedback/session", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          answers,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.success === false) {
        throw new Error(buildErrorMessage(payload, "Failed to save your feedback draft."));
      }

      const nextSession = payload.session || session || {};
      setSession(nextSession);
      setAnswers(normalizeSessionAnswers(nextSession));
      setStatusMessage("Draft saved successfully.");
    } catch (error) {
      setFormError(error.message || "Failed to save your feedback draft.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token || isSubmitted) {
      return;
    }

    stopListening();
    setIsSubmitting(true);
    setFormError("");
    setStatusMessage("");

    try {
      const response = await fetch("/api/meeting/interviewer-feedback/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          answers,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.success === false) {
        throw new Error(buildErrorMessage(payload, "Failed to submit your feedback."));
      }

      const nextSession = payload.feedback || payload.session || session || {};
      setSession(nextSession);
      setAnswers(normalizeSessionAnswers(nextSession));
      setStatusMessage("Feedback submitted successfully. Thank you.");
    } catch (error) {
      setFormError(error.message || "Failed to submit your feedback.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="ifp-shell">
        <div className="ifp-card ifp-centered-card">
          <p className="ifp-kicker">Interviewer Feedback</p>
          <h1>Loading your feedback session...</h1>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="ifp-shell">
        <div className="ifp-card ifp-centered-card">
          <p className="ifp-kicker">Interviewer Feedback</p>
          <h1>We could not open this feedback session.</h1>
          <p className="ifp-message ifp-error">{loadError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ifp-shell">
      <div className="ifp-card">
        <div className="ifp-header">
          <div>
            <p className="ifp-kicker">Interviewer Feedback</p>
            <h1>
              Feedback for {session?.candidate_name || "this interview"}
            </h1>
            <p className="ifp-subtitle">
              Speak your answers or type them directly. Every answer box stays
              visible while you respond.
            </p>
          </div>
          <div className="ifp-meta">
            <span>{session?.interviewer_email || "Secure access link"}</span>
            {session?.job_id ? <span>Job ID: {session.job_id}</span> : null}
            <span>Status: {isSubmitted ? "Submitted" : "Draft"}</span>
          </div>
        </div>

        {!speechRecognitionSupported ? (
          <p className="ifp-message ifp-warning">
            Voice capture is not supported in this browser, so please type your
            answers manually.
          </p>
        ) : null}
        {speechError ? <p className="ifp-message ifp-warning">{speechError}</p> : null}
        {formError ? <p className="ifp-message ifp-error">{formError}</p> : null}
        {statusMessage ? <p className="ifp-message ifp-success">{statusMessage}</p> : null}

        <form className="ifp-form" onSubmit={handleSubmit}>
          {answers.map((item, index) => {
            const wordCount = String(item.answer || "").trim()
              ? String(item.answer || "").trim().split(/\s+/).length
              : 0;
            const isListening = listeningQuestionId === item.id;
            const isSpeaking = speakingQuestionId === item.id;
            const answerId = `feedback-answer-${item.id}`;

            return (
              <section className="ifp-question-card" key={item.id}>
                <div className="ifp-question-head">
                  <div>
                    <p className="ifp-question-index">Question {index + 1}</p>
                    <label className="ifp-question-label" htmlFor={answerId}>
                      {item.label}
                      {item.required ? (
                        <span className="ifp-required" aria-hidden="true">
                          *
                        </span>
                      ) : null}
                    </label>
                    <p className="ifp-question-prompt">{item.question}</p>
                  </div>
                  <div className="ifp-question-actions">
                    <button
                      className="ifp-secondary-button"
                      type="button"
                      onClick={() => toggleReadAloud(item.id, item.question)}
                      disabled={isSubmitted}
                    >
                      {isSpeaking ? "Stop Voice" : "Read Aloud"}
                    </button>
                    <button
                      className={`ifp-mic-button ${isListening ? "ifp-mic-button-active" : ""}`}
                      type="button"
                      aria-label={`Use microphone for ${item.label}`}
                      onClick={() => toggleListening(item.id)}
                      disabled={!speechRecognitionSupported || isSubmitted}
                    >
                      {isListening ? "Stop Mic" : "Use Microphone"}
                    </button>
                  </div>
                </div>

                <textarea
                  id={answerId}
                  value={item.answer}
                  onChange={(event) => handleAnswerChange(item.id, event.target.value)}
                  placeholder="Speak or type your answer here."
                  rows={5}
                  disabled={isSubmitted}
                />

                <div className="ifp-answer-footer">
                  <span>
                    {isListening
                      ? "Listening now. Your spoken answer appears live in this box."
                      : "You can type here or use the microphone for this answer."}
                  </span>
                  <span>{wordCount} words</span>
                </div>
              </section>
            );
          })}

          <div className="ifp-actions">
            <button
              className="ifp-secondary-button"
              type="button"
              onClick={handleSaveDraft}
              disabled={isSaving || isSubmitting || isSubmitted}
            >
              {isSaving ? "Saving..." : "Save Draft"}
            </button>
            <button
              className="ifp-primary-button"
              type="submit"
              disabled={isSaving || isSubmitting || isSubmitted}
            >
              {isSubmitted
                ? "Feedback Submitted"
                : isSubmitting
                ? "Submitting..."
                : "Submit Feedback"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
